import { useAuth } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";
import { CrewNeedsForJob } from "@/components/FieldCrewNeeds";
import { NEED_TYPES, needType, neededLabel, sortNeeds } from "@/components/crewNeeds";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "./_shared";
import { ShoppingCart, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function CrewNeeds() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const selectedJob = params.has("job_id");
  const office = ["admin", "office", "super_admin"].includes(user.role);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({});
  const [groups, setGroups] = useState(null);

  const load = () => api.get("/crew-needs").then((r) => { setGroups(r.data); setError(""); }).catch(() => setError("Crew requests could not be loaded."));
  useEffect(() => { if (!office || selectedJob) return; load(); window.addEventListener("focus", load); return () => window.removeEventListener("focus", load); }, [office, selectedJob]);

  const tick = async (item) => {
    if (busy[item.id]) return;
    setBusy(state => ({...state,[item.id]:true}));
    try {
      await api.patch(`/list-items/${item.id}`, { done: true });
      setGroups(gs => gs.map(g => ({ ...g, items: g.items.filter(i => i.id !== item.id) })).filter(g => g.items.length));
      toast.success("Marked organised / collected");
    } catch { toast.error("Could not update this request. Try again."); }
    finally { setBusy(state => ({...state,[item.id]:false})); }
  };

  const total = (groups || []).reduce((n, g) => n + g.items.length, 0);

  if (!office || params.has("job_id")) return <div className="p-4 lg:p-8 max-w-3xl mx-auto pb-24"><PageHeader title="Crew needs" subtitle="Materials to order, tools to collect and reminders for your crew." />{office && <Link to="/app/crew-needs" className="inline-flex min-h-11 items-center text-sm text-orange-700 dark:text-orange-300">← All crew requests</Link>}<CrewNeedsForJob /></div>;
  const filtered = (groups || []).map(g => ({...g,items:g.items.filter(item => filter === "all" || needType(item) === filter).sort(sortNeeds)})).filter(g=>g.items.length);
  return (
    <div className="p-4 lg:p-10 max-w-4xl mx-auto pb-24 lg:pb-10" data-testid="crew-needs-page">
      <PageHeader
        tag="/ crew needs"
        title="Crew needs"
        subtitle="Shared requests from every job. Tick off once materials are organised or tools collected."
      />
      <Button asChild variant="outline" className="min-h-11"><Link to="/app/crew-needs?job_id=">Add / review a job list</Link></Button>
      <div className="flex flex-wrap gap-2 my-4">{[["all","All requests"],...Object.entries(NEED_TYPES)].map(([key,label])=><Button key={key} variant={filter===key?"secondary":"outline"} className="min-h-11" onClick={()=>setFilter(key)}>{label}</Button>)}</div>
      {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error} <Button variant="outline" onClick={load}>Retry</Button></p>}
      {error ? null : groups === null ? (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-6">Loading…</div>
      ) : total === 0 ? (
        <div className="mt-8">
          <EmptyState title="Nothing needed right now" description="When crews add items to a job's Needs list, they'll show up here." />
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {filtered.length === 0 && total > 0 && <p className="text-sm text-muted-foreground">No open requests in this category.</p>}
          {filtered.map((g) => (
            <section key={g.job_id} className="hard-card p-5" data-testid={`crew-needs-job-${g.job_id}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="min-w-0">
                  {g.job_number && (
                    <span className="font-mono text-[11px] font-bold text-[#FF5F15] tracking-widest mr-2">{g.job_number}</span>
                  )}
                  <span className="font-display font-black uppercase tracking-tight text-sm">{g.job_title}</span>
                </div>
                <Link to={`/app/crew-needs?job_id=${encodeURIComponent(g.job_id)}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] shrink-0"
                      data-testid={`crew-needs-open-job-${g.job_id}`}>
                  Open list <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <ul className="space-y-1.5">
                {g.items.map((item) => (
                  <li key={item.id} className="flex items-start gap-2.5 p-2.5 border border-zinc-300 dark:border-zinc-700 rounded-sm"
                      data-testid={`crew-need-item-${item.id}`}>
                    <button type="button" disabled={!!busy[item.id]} onClick={() => tick(item)}
                            className="mt-0.5 w-11 h-11 shrink-0 rounded-sm border-2 border-zinc-400 hover:border-emerald-600 hover:bg-emerald-600 hover:text-white text-transparent flex items-center justify-center transition-colors"
                            data-testid={`crew-need-tick-${item.id}`} aria-label={`Mark organised or collected: ${item.text}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm break-words">{item.text}</div>
                      <p className="text-xs font-medium text-orange-700 dark:text-orange-300 mt-1">{NEED_TYPES[needType(item)]} · {neededLabel(item)}</p>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {item.created_by_name} · {new Date(item.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
                      </div>
                    </div>
                    <ShoppingCart className="w-4 h-4 text-zinc-300 shrink-0 mt-0.5" />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
